/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Utils
import { groupBy } from 'lodash';

/**
 * @typedef {object} PersonActivity
 * @prop {'person'} fp_type
 * @prop {{ timestamp: number }} fp_last_changes unix timestamp
 *
 * @typedef {object} ActRecordActivity
 * @prop {'actrecord'} fp_type
 * @prop {number} timestamp js timestamp
 *
 * @typedef {object} EmailActivity
 * @prop {'console.log'} fp_type
 * @prop {number} timestamp
 * @prop {object} message
 * @prop {string} message.subject
 * @prop {{ name: string, content: string }[]} message.global_merge_vars
 * @prop {{ vars: { name: string, content: string }[] }[]} message.merge_vars
 *
 * @typedef {PersonActivity|ActRecordActivity|EmailActivity} Activity
 *
 * @typedef {object} Activation
 * @prop {string} activation_id
 * @prop {string} activation_uuid
 * @prop {string} activated_on a stringified date
 * @prop {string} platform
 * @prop {string} node
 * @prop {string} user_agent
 * @prop {string[]} accepted_legal_documents
 *
 * @typedef {object} Row
 * @prop {string} date
 * @prop {string} icon
 * @prop {string} label
 * @prop {{ detailLabel: string, values: { valueLabel: string, href?: string }[]}} [details] only for activations
 */

const __vue_script__ = {
    name: 'PersonActivityFeed',

    props: {
        eid: {
            type: String,
            required: true,
        },
        pid: {
            type: String,
            required: true,
        },
        timeline: {
            type: Array,
            default: () => [],
        },
        timezone: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            rows: [],
            days: []
        };
    },

    async mounted() {
        /** @type {Row[]} */
        const rows = [];
        /** @type {Activity[]} */
        const activities = this.timeline;
        /** @type {ActivationActivity[]} */
        const activations = [];

        for (const d of activities) {
            if (d.fp_type === 'actrecord') {
                activations.push(d);
            } else if (d.fp_type === 'person') {
                rows.push({
                    icon: 'ic-edit',
                    label: this.$t('user_display.participant_record_updated'),
                    date: this.formatTimestamp(d.fp_last_changes.timestamp),
                    timestamp: d.fp_last_changes.timestamp,
                });
            } else if (d.fp_type === 'console.log') {
                const timestamp = Math.round(d.timestamp / 1000);
                rows.push({
                    icon: 'ic-mail',
                    label: this.$t('user_display.mail_delivered', [d.status || 'sent', this.getMailSubject(d)]),
                    // this one is a js timestamp, as opposed to that of "person" that is a unix timestamp
                    date: this.formatTimestamp(timestamp),
                    timestamp
                });
            }
        }

        if (activations.length) {
            // activations need to be gotten separately, those in "timeline" do not contain necessary info
            for (const row of await this.getActivationRows()) {
                rows.push(row);
            }
        }

        this.rows = rows.sort((a, b) => a.date > b.date ? 1 : -1);

        const groupedDays = groupBy(this.rows, row => this.$utils.time.convertTimestampToHumanForm(row.timestamp, this.timezone, 'LL'));
        const unsortedDays = [];

        for (const [day, rows] of Object.entries(groupedDays)) {
            unsortedDays.push({
                day,
                rows: rows.sort((a, b) => a.timestamp > b.timestamp ? 1 : -1)
            });
        }

        this.days = unsortedDays.sort((a, b) => a.rows[0].timestamp > b.rows[0].timestamp ? -1 : 1);
    },

    methods: {
        /** @type {(d: number) => string} */
        formatTimestamp(d) {
            return this.$utils.time.convertTimestampToHumanForm(d, this.timezone, 'LT');
        },

        /** @type {(d: EmailActivity) => string} */
        getMailSubject({ message }) {
            let subject = message.subject;

            const globs = !Array.isArray(message.global_merge_vars) ? [message.global_merge_vars] : message.global_merge_vars;
            for (const glob of globs) {
                subject = subject.replaceAll(`{{${glob.name}}}`, glob.content);
            }

            const merges = !Array.isArray(message.merge_vars) ? [message.merge_vars] : message.merge_vars;
            for (let { vars } of merges) {
                const varAry = !Array.isArray(vars) ? [vars] : vars;
                for (const variable of varAry) {
                    subject = subject.replaceAll(`{{${variable.name}}}`, variable.content);
                }
            }

            return subject;
        },

        /** @returns {Promise<Row[]>} */
        async getActivationRows() {
            const rows = [];
            try {
                /** @type {{ activations: Activation[] }} */
                const { activations } = await this.$services.appScripts.callAppScript(
                    this.eid,
                    'participants/person-activations',
                    { pid: this.pid },
                );

                for (const d of activations) {
                    const label = d.platform === 'web'
                        ? this.$t('user_display.web_activation')
                        : this.$t('user_display.mobile_activation');

                    const date = this.formatTimestamp(this.$utils.time.dateToUnix(new Date(d.activated_on)));
                    /** @type {Row['details']} */
                    const details = Object.keys(d).map(key => {
                        const detailLabel =
                            this.$te(`user_display.activation.${key}`) ?
                                this.$t(`user_display.activation.${key}`) :
                                this.$utils.strings.humanize(key, true);

                        let values =
                            key === 'accepted_legal_documents' ?
                                d.accepted_legal_documents.map(legalId => ({
                                    valueLabel: legalId,
                                    href: `/api/v1/legal/document/${legalId}.html`,
                                })) :
                                [{ valueLabel: d[key] }];

                        if (key === 'activated_on') {
                            values = [{ valueLabel: this.$utils.time.dateToFormat(d[key], 'LLLL') }];
                        }

                        return { detailLabel, values };
                    });

                    const timestamp = Math.round(new Date(d.activated_on).getTime() / 1000);
                    rows.push({ icon: 'ic-screen', label, date, details, timestamp });
                }
                return rows;
            } catch (err) {
                console.error('[PersonActivityFeed] could not get activations', err);
                return rows;
            }
        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"person-activity-feed"},_vm._l((_vm.days),function(ref){
var rows = ref.rows;
var day = ref.day;
return _c('div',{staticClass:"days"},[_c('h5',[_vm._v(_vm._s(day))]),_c('div',{staticClass:"activities"},_vm._l((rows),function(row){return _c('div',{staticClass:"activity"},[_c('i',{class:row.icon}),(row.details)?_c('details',[_c('summary',[_c('span',{staticClass:"date"},[_vm._v(_vm._s(row.date))]),_vm._v(_vm._s(row.label))]),_c('dl',[_vm._l((row.details),function(detail){return [_c('dt',[_vm._v(_vm._s(detail.detailLabel))]),_vm._l((detail.values),function(d){return _c('dd',[(d.href)?_c('a',{attrs:{"href":d.href,"target":"_blank","rel":"noopener noreferrer"}},[_vm._v(_vm._s(d.valueLabel))]):_c('span',[_vm._v(_vm._s(d.valueLabel))])])})]})],2)]):_c('p',[_c('span',{staticClass:"date"},[_vm._v(_vm._s(row.date))]),_vm._v(_vm._s(row.label))])])}),0)])}),0)}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "PersonActivityFeed.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__